import { useEffect, useState, useRef } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { sharedSupabase } from "@/integrations/supabase/sharedClient";
import { getR90Grade, getXGGrade, getXAGrade, getRegainsGrade, getInterceptionsGrade, getXGChainGrade, getProgressivePassesGrade, getPPTurnoversRatioGrade } from "@/lib/gradeCalculations";
import { Download, X, ImageIcon, Video, Play, Calculator, TrendingUp, BarChart3, Film, Award } from "lucide-react";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import { ActionVideoPopup } from "@/components/ActionVideoPopup";
import { ClippedActionsPlayer } from "@/components/ClippedActionsPlayer";
import { STAT_TYPE_CONFIGS, StatTypeConfig } from "@/components/staff/ActionStatRecorder";
import { R90FlowChart } from "@/components/report/R90FlowChart";
import { ActionHeatmap } from "@/components/report/ActionHeatmap";
import { ChanceCreationFlow } from "@/components/report/ChanceCreationFlow";
import { RankedActionsPlayer } from "@/components/report/RankedActionsPlayer";

// Format minute as MM.SS with proper zero padding (e.g., 0.3 → "0.30", 10.5 → "10.50")
const formatMinute = (minute: number | null | undefined): string => {
  if (minute === null || minute === undefined) return "-";
  const minPart = Math.floor(minute);
  const secPart = Math.round((minute - minPart) * 100);
  return `${minPart}.${secPart.toString().padStart(2, '0')}`;
};

interface PerformanceAction {
  id: string;
  action_number: number;
  minute: number;
  action_score: number;
  action_type: string;
  action_description: string;
  notes: string | null;
  video_url?: string | null;
}

interface StrikerStats {
  [key: string]: number | string | any[] | undefined;
}

interface AnalysisDetails {
  id: string;
  analysis_date: string;
  opponent: string;
  result: string;
  r90_score: number | null;
  minutes_played: number | null;
  player_name: string;
  striker_stats?: StrikerStats | null;
  performance_overview?: string | null;
}

interface PerformanceReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  analysisId: string | null;
}

export const PerformanceReportDialog = ({ open, onOpenChange, analysisId }: PerformanceReportDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState<AnalysisDetails | null>(null);
  const [actions, setActions] = useState<PerformanceAction[]>([]);
  const [prefetchedId, setPrefetchedId] = useState<string | null>(null);
  const [savingImage, setSavingImage] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState<string | null>(null);
  const [selectedVideoTitle, setSelectedVideoTitle] = useState<string>("");
  const [showR90Flow, setShowR90Flow] = useState(false);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [showChanceCreation, setShowChanceCreation] = useState(false);
  const [showRankedPlayer, setShowRankedPlayer] = useState(false);
  const [rankedMode, setRankedMode] = useState<"chronological" | "ranked">("chronological");
  const [showClippedActions, setShowClippedActions] = useState(false);

  // Pre-fetch data when analysisId changes (even before dialog opens)
  useEffect(() => {
    if (analysisId && analysisId !== prefetchedId) {
      fetchPerformanceData(analysisId);
    }
  }, [analysisId]);

  // Re-fetch if dialog opens with a different ID than what's cached
  useEffect(() => {
    if (open && analysisId && analysisId !== prefetchedId) {
      fetchPerformanceData(analysisId);
    }
  }, [open, analysisId, prefetchedId]);

  const fetchPerformanceData = async (id: string) => {
    if (!id) return;

    setLoading(true);
    try {
      const [analysisResult, actionsResult] = await Promise.all([
        sharedSupabase
          .from("player_analysis")
          .select(`
            *,
            players (name)
          `)
          .eq("id", id)
          .maybeSingle(),
        sharedSupabase
          .from("performance_report_actions")
          .select("*")
          .eq("analysis_id", id)
          .order("action_number", { ascending: true })
      ]);

      if (analysisResult.error) throw analysisResult.error;

      if (analysisResult.data) {
        setAnalysis({
          id: analysisResult.data.id,
          analysis_date: analysisResult.data.analysis_date,
          opponent: analysisResult.data.opponent || "",
          result: analysisResult.data.result || "",
          r90_score: analysisResult.data.r90_score,
          minutes_played: analysisResult.data.minutes_played,
          player_name: analysisResult.data.players?.name || "Unknown Player",
          striker_stats: analysisResult.data.striker_stats as StrikerStats | null,
          performance_overview: analysisResult.data.performance_overview,
        });
      } else {
        setAnalysis(null);
      }

      if (actionsResult.error) throw actionsResult.error;
      setActions(actionsResult.data || []);

      setPrefetchedId(id);
    } catch (error: any) {
      console.error("Error fetching performance data:", error);
      toast.error("Failed to load performance report");
    } finally {
      setLoading(false);
    }
  };

  const getActionScoreColor = (score: number) => {
    if (score >= 0.15) return "text-green-800 font-bold";
    if (score >= 0.1) return "text-green-600 font-bold";
    if (score >= 0.05) return "text-green-500 font-semibold";
    if (score >= 0.02) return "text-green-400";
    if (score > 0.005) return "text-lime-500";
    if (score > 0) return "text-lime-400";
    if (score === 0) return "text-muted-foreground";
    if (score > -0.005) return "text-orange-400";
    if (score > -0.02) return "text-orange-500";
    if (score > -0.04) return "text-red-400";
    if (score > -0.06) return "text-red-500 font-semibold";
    return "text-red-700 font-bold";
  };

  const calculateRScore = (): number => {
    return actions.reduce((sum, action) => sum + (action.action_score ?? 0), 0);
  };

  const calculateXGChain = (): number => {
    return actions.reduce((sum, action) => {
      const score = action.action_score ?? 0;
      return score > 0 ? sum + score : sum;
    }, 0);
  };

  const handleSaveAsWebp = async () => {
    if (!contentRef.current || !analysis) return;

    setSavingImage(true);
    try {
      const originalBg = contentRef.current.style.backgroundColor;
      contentRef.current.style.backgroundColor = '#000000';

      const canvas = await html2canvas(contentRef.current, {
        backgroundColor: '#000000',
        useCORS: true,
        logging: false,
        scale: 2,
      } as any);

      contentRef.current.style.backgroundColor = originalBg;

      const fileName = `${analysis.player_name}-vs-${analysis.opponent}-performance-report`;
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth < 768;

      if (isMobile) {
        const dataUrl = canvas.toDataURL('image/png', 0.95);
        if (!dataUrl || dataUrl === 'data:,') {
          toast.error('Failed to create image');
          return;
        }
        const newTab = window.open();
        if (newTab) {
          newTab.document.write(`<html><head><title>${fileName}</title><style>body{margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#000;}</style></head><body><img src="${dataUrl}" style="max-width:100%;height:auto;" /></body></html>`);
          newTab.document.close();
          toast.success('Image opened - long-press to save');
        } else {
          const link = document.createElement('a');
          link.href = dataUrl;
          link.download = `${fileName}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          toast.success('Image saved');
        }
      } else {
        const dataUrl = canvas.toDataURL('image/webp', 0.9);
        const link = document.createElement('a');
        link.download = `${fileName}.webp`;
        link.href = dataUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Image saved successfully');
      }
    } catch (error) {
      console.error('Error saving image:', error);
      toast.error('Failed to save image');
    } finally {
      setSavingImage(false);
    }
  };

  // Format stat key to readable label using config lookup
  const formatStatLabel = (key: string): string => {
    let config = STAT_TYPE_CONFIGS.find((c: StatTypeConfig) => c.key === key);
    if (config) return config.name;
    const keyLower = key.toLowerCase();
    config = STAT_TYPE_CONFIGS.find((c: StatTypeConfig) => c.key.toLowerCase() === keyLower);
    if (config) return config.name;
    return key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  // Get advanced stats from striker_stats, excluding internal fields
  const getAdvancedStats = () => {
    if (!analysis?.striker_stats) return [];

    const excludeKeys = ['selected_stats', 'stats_order'];
    const stats: {
      key: string;
      value: number | string;
      per90Value?: number | string;
      isPaired?: boolean;
      successful?: number;
      attempted?: number;
      percentage?: string;
    }[] = [];
    const processedKeys = new Set<string>();

    const statsOrder = analysis.striker_stats.stats_order as string[] | undefined;
    const selectedStats = analysis.striker_stats.selected_stats as string[] | undefined;
    const rawKeysToShow = statsOrder || selectedStats || Object.keys(analysis.striker_stats);
    const keysToShow = rawKeysToShow.filter(key => !excludeKeys.includes(key));

    for (const key of keysToShow) {
      if (key.includes('_per90')) continue;
      if (processedKeys.has(key)) continue;

      const value = analysis.striker_stats[key];
      if (value === null || value === undefined) continue;
      if (typeof value === 'string' && value.trim() === '') continue;
      if (typeof value === 'number' && isNaN(value)) continue;

      let attemptedKey = `${key}_attempted`;
      let baseKey = key;

      if (key.endsWith('_won')) {
        baseKey = key.replace('_won', '');
        attemptedKey = `${baseKey}_attempted`;
      } else if (key.endsWith('_completed')) {
        baseKey = key.replace('_completed', '');
        attemptedKey = `${baseKey}_attempted`;
      }

      if (analysis.striker_stats[attemptedKey] != null && !key.endsWith('_attempted')) {
        const attempted = Number(analysis.striker_stats[attemptedKey]);
        const successful = Number(value);
        if (!isNaN(attempted) && !isNaN(successful)) {
          processedKeys.add(attemptedKey);
          const per90Key = `${key}_per90`;
          const per90Value = analysis.striker_stats[per90Key];
          stats.push({
            key: baseKey !== key ? baseKey : key,
            value: successful,
            per90Value: per90Value !== null && per90Value !== undefined ? per90Value as number | string : undefined,
            isPaired: true,
            successful,
            attempted,
            percentage: attempted > 0 ? ((successful / attempted) * 100).toFixed(1) : '0'
          });
          continue;
        }
      }

      if (key.endsWith('_attempted')) {
        processedKeys.add(key);
        continue;
      }

      if (typeof value !== 'number' && typeof value !== 'string') continue;

      const keyLower = key.toLowerCase();
      const rateBasedPrefixes = ['xg', 'xa', 'xc', 'xgchain'];
      const isRateBased = rateBasedPrefixes.some(prefix => keyLower.includes(prefix));
      const per90Key = `${key}_per90`;
      const per90Value = isRateBased ? analysis.striker_stats[per90Key] : undefined;

      stats.push({
        key,
        value,
        per90Value: per90Value !== null && per90Value !== undefined ? per90Value as number | string : undefined
      });
    }

    return stats;
  };

  // Calculate derived stats from the base stats
  const getCalculatedStats = () => {
    if (!analysis?.striker_stats) return [];

    const strikerStats = analysis.striker_stats;
    const calculated: { key: string; displayName: string; value: number; description: string }[] = [];

    const getVal = (key: string): number | null => {
      const val = strikerStats[key];
      if (val === null || val === undefined) return null;
      return typeof val === 'number' ? val : null;
    };

    const getSuccessVal = (baseKey: string): number | null => {
      return getVal(`${baseKey}_successful`) ?? getVal(baseKey);
    };

    const getTotalVal = (baseKey: string): number | null => {
      return getVal(`${baseKey}_total`) ?? getVal(`${baseKey}_attempted`);
    };

    const recoveries = getVal('recoveries');
    const turnovers = getVal('turnovers');
    if (recoveries !== null && turnovers !== null) {
      const ratio = turnovers === 0 ? (recoveries > 0 ? recoveries : 0) : recoveries / turnovers;
      calculated.push({ key: 'recovery_turnover_ratio', displayName: 'Recovery/Turnover', value: ratio, description: 'Recoveries ÷ Turnovers' });
    }

    const ppSuccess = getSuccessVal('progressive_passes');
    if (ppSuccess !== null && turnovers !== null) {
      const ratio = turnovers === 0 ? (ppSuccess > 0 ? ppSuccess : 0) : ppSuccess / turnovers;
      calculated.push({ key: 'pp_turnovers_ratio', displayName: 'PP/Turnovers', value: ratio, description: 'Progressive Passes ÷ Turnovers' });
    }

    const aerialSuccess = getSuccessVal('aerial_duels');
    const aerialTotal = getTotalVal('aerial_duels');
    if (aerialSuccess !== null && aerialTotal !== null && aerialTotal > 0) {
      calculated.push({ key: 'aerial_duel_win_pct', displayName: 'Aerial Duel Win %', value: (aerialSuccess / aerialTotal) * 100, description: 'Aerial Duels Won ÷ Total' });
    }

    const passSuccess = getSuccessVal('passes');
    const passTotal = getTotalVal('passes');
    if (passSuccess !== null && passTotal !== null && passTotal > 0) {
      calculated.push({ key: 'pass_completion', displayName: 'Pass Completion %', value: (passSuccess / passTotal) * 100, description: 'Passes Completed ÷ Total' });
    }

    const dribbleSuccess = getSuccessVal('dribbles');
    const dribbleTotal = getTotalVal('dribbles');
    if (dribbleSuccess !== null && dribbleTotal !== null && dribbleTotal > 0) {
      calculated.push({ key: 'dribble_success_pct', displayName: 'Dribble Success %', value: (dribbleSuccess / dribbleTotal) * 100, description: 'Dribbles Completed ÷ Total' });
    }

    const tackleSuccess = getSuccessVal('tackles');
    const tackleTotal = getTotalVal('tackles');
    if (tackleSuccess !== null && tackleTotal !== null && tackleTotal > 0) {
      calculated.push({ key: 'tackle_success_pct', displayName: 'Tackle Success %', value: (tackleSuccess / tackleTotal) * 100, description: 'Tackles Won ÷ Total' });
    }

    const xg = getVal('xg');
    const shotsTotal = getTotalVal('shots') ?? getVal('shots');
    if (xg !== null && shotsTotal !== null && shotsTotal > 0) {
      calculated.push({ key: 'xg_per_shot', displayName: 'xG per Shot', value: xg / shotsTotal, description: 'xG ÷ Total Shots' });
    }

    return calculated;
  };

  const advancedStats = getAdvancedStats();
  const calculatedStats = getCalculatedStats();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[98vw] md:max-w-[95vw] w-full max-h-[95vh] overflow-y-auto overflow-x-hidden p-0">
        <div className="sticky top-0 z-10 bg-background border-b p-2 md:p-4 flex items-center justify-between gap-2">
          <h2 className="text-base md:text-xl font-bebas uppercase tracking-wider truncate">Performance Report</h2>
          <div className="flex gap-1 md:gap-2 flex-shrink-0">
            <Button onClick={handleSaveAsWebp} variant="default" size="sm" className="px-2 md:px-3" disabled={savingImage || loading}>
              <ImageIcon className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">{savingImage ? 'Saving...' : 'Save'}</span>
            </Button>
            <Button onClick={() => onOpenChange(false)} variant="outline" size="sm" className="px-2 md:px-3">
              <X className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">Close</span>
            </Button>
          </div>
        </div>

        <div className="p-3 md:p-6 overflow-x-hidden">
          {loading ? (
            <div className="space-y-6 animate-pulse">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i}>
                    <div className="h-4 w-16 bg-muted rounded mb-2"></div>
                    <div className="h-6 w-24 bg-muted rounded"></div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-accent/20 rounded-lg">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="text-center">
                    <div className="h-4 w-16 bg-muted rounded mx-auto mb-2"></div>
                    <div className="h-8 w-20 bg-muted rounded mx-auto"></div>
                  </div>
                ))}
              </div>
              <div className="h-40 bg-muted rounded"></div>
            </div>
          ) : !analysis ? (
            <div className="text-center py-8 text-muted-foreground">Performance report not found</div>
          ) : (
            <div ref={contentRef} className="space-y-4 md:space-y-6 bg-background p-2 md:p-4 rounded-lg overflow-x-hidden">
              {/* Player Info with Clipped Actions Button */}
              <div className="flex flex-col gap-3">
                <div className="grid grid-cols-2 gap-2 md:grid-cols-4 md:gap-4">
                  <div>
                    <p className="text-xs md:text-sm text-muted-foreground">Player</p>
                    <p className="font-bold text-sm md:text-base truncate">{analysis.player_name}</p>
                  </div>
                  <div>
                    <p className="text-xs md:text-sm text-muted-foreground">Date</p>
                    <p className="font-bold text-sm md:text-base">{new Date(analysis.analysis_date).toLocaleDateString('en-GB')}</p>
                  </div>
                  <div>
                    <p className="text-xs md:text-sm text-muted-foreground">Opponent</p>
                    <p className="font-bold text-sm md:text-base truncate">{analysis.opponent || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-xs md:text-sm text-muted-foreground">Result</p>
                    <p className="font-bold text-sm md:text-base">{analysis.result || "N/A"}</p>
                  </div>
                </div>

                {/* Clipped Actions Button */}
                {actions.filter(a => a.video_url).length > 0 && (
                  <Button
                    variant="default"
                    size="sm"
                    className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold flex items-center gap-2"
                    onClick={() => setShowClippedActions(true)}
                  >
                    <Play className="h-4 w-4" />
                    {actions.filter(a => a.video_url).length}
                  </Button>
                )}
              </div>

              {/* Graphics Buttons Row */}
              {actions.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant={showR90Flow ? "default" : "outline"}
                    size="sm"
                    onClick={() => { setShowR90Flow(!showR90Flow); setShowHeatmap(false); }}
                    className="text-xs"
                  >
                    <TrendingUp className="h-3.5 w-3.5 mr-1.5" />
                    R90 Flow
                  </Button>
                  <Button
                    variant={showHeatmap ? "default" : "outline"}
                    size="sm"
                    onClick={() => { setShowHeatmap(!showHeatmap); setShowR90Flow(false); setShowChanceCreation(false); }}
                    className="text-xs"
                  >
                    <BarChart3 className="h-3.5 w-3.5 mr-1.5" />
                    Period Grade Map
                  </Button>
                  {analysis.striker_stats && ['crossing_movement_xC', 'movement_in_behind_xC', 'movement_down_side_xC', 'triple_threat_xC', 'movement_to_feet_xC'].some(k => (analysis.striker_stats as any)?.[k] > 0) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setShowChanceCreation(!showChanceCreation); setShowR90Flow(false); setShowHeatmap(false); }}
                      className="text-xs"
                    >
                      <TrendingUp className="h-3.5 w-3.5 mr-1.5" />
                      Chance Creation Flow
                    </Button>
                  )}
                  {actions.filter(a => a.video_url).length > 0 && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setRankedMode("chronological"); setShowRankedPlayer(true); }}
                        className="text-xs"
                      >
                        <Film className="h-3.5 w-3.5 mr-1.5" />
                        Full Match Video
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setRankedMode("ranked"); setShowRankedPlayer(true); }}
                        className="text-xs"
                      >
                        <Award className="h-3.5 w-3.5 mr-1.5" />
                        Ranked Actions
                      </Button>
                    </>
                  )}
                </div>
              )}

              {/* R90 Flow Chart */}
              {showR90Flow && analysis.minutes_played && (
                <Card className="overflow-hidden">
                  <CardContent className="p-3 md:p-6">
                    <R90FlowChart actions={actions} minutesPlayed={analysis.minutes_played} />
                  </CardContent>
                </Card>
              )}

              {/* Action Heatmap */}
              {showHeatmap && analysis.minutes_played && (
                <Card className="overflow-hidden">
                  <CardContent className="p-3 md:p-6">
                    <ActionHeatmap actions={actions} minutesPlayed={analysis.minutes_played} />
                  </CardContent>
                </Card>
              )}

              {/* Chance Creation Flow */}
              {showChanceCreation && analysis.striker_stats && (
                <Card className="overflow-hidden">
                  <CardContent className="p-3 md:p-6">
                    <ChanceCreationFlow strikerStats={analysis.striker_stats as Record<string, any>} />
                  </CardContent>
                </Card>
              )}

              {/* Key Stats */}
              <div className="grid grid-cols-2 gap-2 md:grid-cols-4 md:gap-4 p-2 md:p-4 bg-accent/20 rounded-lg">
                <div className="text-center p-2">
                  <p className="text-[10px] md:text-sm text-muted-foreground mb-0.5 md:mb-1">Raw Score</p>
                  <p className="text-base md:text-2xl font-bold">
                    {actions.length > 0 ? calculateRScore().toFixed(3) : (analysis.r90_score !== null && analysis.minutes_played ? ((analysis.r90_score / 90) * analysis.minutes_played).toFixed(3) : "N/A")}
                  </p>
                </div>
                <div className="text-center bg-primary text-primary-foreground rounded-lg p-2 md:p-4">
                  <p className="text-[10px] md:text-sm mb-0.5 md:mb-1 opacity-90">R90</p>
                  <p className="text-lg md:text-3xl font-bold">
                    {analysis.r90_score !== null
                      ? analysis.r90_score.toFixed(2)
                      : analysis.minutes_played && actions.length > 0
                        ? ((calculateRScore() / analysis.minutes_played) * 90).toFixed(2)
                        : "N/A"
                    }
                  </p>
                </div>
                <div className="text-center p-2">
                  <p className="text-[10px] md:text-sm text-muted-foreground mb-0.5 md:mb-1">xG Chain</p>
                  <p className="text-base md:text-2xl font-bold">{actions.length > 0 ? calculateXGChain().toFixed(2) : "N/A"}</p>
                </div>
                <div className="text-center p-2">
                  <p className="text-[10px] md:text-sm text-muted-foreground mb-0.5 md:mb-1">Mins</p>
                  <p className="text-base md:text-2xl font-bold">{analysis.minutes_played ?? "N/A"}</p>
                </div>
              </div>

              {/* Advanced Stats */}
              {advancedStats.length > 0 && (
                <Card className="overflow-hidden">
                  <CardHeader className="py-2 md:py-4">
                    <CardTitle className="text-sm md:text-lg">Match Statistics</CardTitle>
                  </CardHeader>
                  <CardContent className="p-2 md:p-6">
                    <div className="grid grid-cols-3 gap-1 md:grid-cols-4 lg:grid-cols-6 md:gap-4">
                      {advancedStats.map((stat) => (
                        <div key={stat.key} className="text-center p-1.5 md:p-3 bg-accent/10 rounded">
                          <p className="text-[9px] md:text-xs text-muted-foreground mb-0.5 capitalize truncate">{formatStatLabel(stat.key)}</p>
                          {stat.isPaired ? (
                            <>
                              <p className="text-sm md:text-lg font-bold">{stat.percentage}%</p>
                              <p className="text-[9px] md:text-xs text-muted-foreground">{stat.successful}/{stat.attempted}</p>
                            </>
                          ) : (
                            <p className="text-sm md:text-lg font-bold">{stat.value}</p>
                          )}
                          {stat.per90Value !== undefined && (
                            <p className="text-[8px] md:text-xs text-muted-foreground mt-0.5">
                              p90: {stat.per90Value}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Auto-Calculated Ratios */}
              {calculatedStats.length > 0 && (
                <Card className="overflow-hidden border-primary/20">
                  <CardHeader className="py-2 md:py-4 bg-primary/5">
                    <CardTitle className="text-sm md:text-lg flex items-center gap-2">
                      <Calculator className="h-4 w-4 text-primary" />
                      <span className="text-primary">Calculated Ratios</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-2 md:p-6">
                    <div className="grid grid-cols-3 gap-1 md:grid-cols-4 lg:grid-cols-6 md:gap-4">
                      {calculatedStats.map((stat) => (
                        <div key={stat.key} className="text-center p-1.5 md:p-3 bg-primary/5 rounded border border-primary/10">
                          <p className="text-[9px] md:text-xs text-muted-foreground mb-0.5 truncate" title={stat.description}>
                            {stat.displayName}
                          </p>
                          <p className="text-sm md:text-lg font-bold text-primary">
                            {stat.key.includes('pct') || stat.key.includes('completion') || stat.key.includes('success')
                              ? `${stat.value.toFixed(1)}%`
                              : stat.value.toFixed(2)}
                          </p>
                          <p className="text-[8px] md:text-[10px] text-muted-foreground mt-0.5">
                            {stat.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Performance Overview */}
              {analysis.performance_overview && (
                <Card className="overflow-hidden">
                  <CardHeader className="py-2 md:py-4">
                    <CardTitle className="text-sm md:text-lg">Overview</CardTitle>
                  </CardHeader>
                  <CardContent className="p-2 md:p-6">
                    <p className="text-muted-foreground whitespace-pre-wrap text-center text-xs md:text-sm">{analysis.performance_overview}</p>
                  </CardContent>
                </Card>
              )}

              {/* Performance Actions */}
              {actions.length > 0 && (
                <Card className="overflow-hidden">
                  <CardHeader className="py-2 md:py-4">
                    <CardTitle className="text-sm md:text-lg">Actions ({actions.length})</CardTitle>
                  </CardHeader>
                  <CardContent className="p-2 md:p-6">
                    {/* Mobile: Compact card layout */}
                    <div className="block md:hidden space-y-2">
                      {actions.map((action) => (
                        <div key={action.id} className="p-2 bg-muted/30 rounded">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <span className="font-semibold text-xs">#{action.action_number}</span>
                              <span className="text-[10px] text-muted-foreground">{formatMinute(action.minute)}'</span>
                              <span className={`text-xs font-bold ${getActionScoreColor(action.action_score)}`}>
                                {action.action_score?.toFixed(3)}
                              </span>
                            </div>
                            {action.video_url && (
                              <button
                                onClick={() => {
                                  setSelectedVideoUrl(action.video_url!);
                                  setSelectedVideoTitle(`#${action.action_number} - ${action.action_type}`);
                                }}
                                className="text-accent hover:text-accent/80 p-0.5 flex-shrink-0"
                              >
                                <Video className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                          <div className="font-medium text-xs mt-1 truncate">{action.action_type}</div>
                          <div className="text-[10px] text-foreground/80 line-clamp-2">{action.action_description}</div>
                          {action.notes && (
                            <div className="text-[9px] text-muted-foreground italic mt-1 pt-1 border-t border-border/50 truncate">
                              {action.notes}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Desktop: Table layout */}
                    <div className="hidden md:block overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2 px-2">#</th>
                            <th className="text-left py-2 px-2">Min</th>
                            <th className="text-left py-2 px-2">Type</th>
                            <th className="text-left py-2 px-2">Description</th>
                            <th className="text-left py-2 px-2">Notes</th>
                            <th className="text-right py-2 px-2">Score</th>
                            <th className="text-center py-2 px-2">Clip</th>
                          </tr>
                        </thead>
                        <tbody>
                          {actions.map((action) => (
                            <tr key={action.id} className="border-b border-border/50">
                              <td className="py-2 px-2">{action.action_number}</td>
                              <td className="py-2 px-2">{formatMinute(action.minute)}'</td>
                              <td className="py-2 px-2">{action.action_type}</td>
                              <td className="py-2 px-2">{action.action_description}</td>
                              <td className="py-2 px-2 text-muted-foreground">{action.notes || "-"}</td>
                              <td className={`py-2 px-2 text-right ${getActionScoreColor(action.action_score)}`}>
                                {action.action_score?.toFixed(5)}
                              </td>
                              <td className="py-2 px-2 text-center">
                                {action.video_url ? (
                                  <button
                                    onClick={() => {
                                      setSelectedVideoUrl(action.video_url!);
                                      setSelectedVideoTitle(`#${action.action_number} - ${action.action_type}`);
                                    }}
                                    className="text-accent hover:text-accent/80 p-1"
                                  >
                                    <Video className="h-4 w-4" />
                                  </button>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </DialogContent>

      {/* Video Popup for single action */}
      {selectedVideoUrl && (
        <ActionVideoPopup
          open={!!selectedVideoUrl}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedVideoUrl(null);
              setSelectedVideoTitle("");
            }
          }}
          videoUrl={selectedVideoUrl}
          actionTitle={selectedVideoTitle}
        />
      )}

      {/* Clipped Actions Player */}
      <ClippedActionsPlayer
        open={showClippedActions}
        onOpenChange={setShowClippedActions}
        clips={actions
          .filter(a => a.video_url)
          .map(a => ({
            id: a.id,
            action_number: a.action_number,
            action_type: a.action_type,
            action_description: a.action_description,
            video_url: a.video_url!,
            minute: a.minute,
          }))}
      />

      {/* Ranked/Full Match Video Player */}
      <RankedActionsPlayer
        open={showRankedPlayer}
        onOpenChange={setShowRankedPlayer}
        mode={rankedMode}
        clips={actions
          .filter(a => a.video_url)
          .map(a => ({
            id: a.id,
            action_number: a.action_number,
            action_type: a.action_type,
            action_description: a.action_description,
            action_score: a.action_score,
            video_url: a.video_url!,
            minute: a.minute,
          }))}
      />
    </Dialog>
  );
};
